import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Flame, MessageCircle, Sparkles, TrendingUp } from "lucide-react";
import { AppLayout } from "../shared/layout";
import { Card } from "@/components/ui/card";
import { getPracticeSessions } from "@/data/requests/practice-requests";

function overallOf(session) {
  if (!session.feedback) return null;
  return Math.round((session.feedback.clarityScore + session.feedback.empathyScore + session.feedback.boundaryScore) / 3);
}

function computeStreak(sessions) {
  const days = new Set(sessions.map((session) => new Date(session.startedAt).toDateString()));
  let streak = 0;
  const date = new Date();
  if (!days.has(date.toDateString())) date.setDate(date.getDate() - 1);
  while (days.has(date.toDateString())) {
    streak += 1;
    date.setDate(date.getDate() - 1);
  }
  return streak;
}

export function ProgressScreen() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({ queryKey: ["practice-sessions"], queryFn: getPracticeSessions });
  const stats = useMemo(() => {
    const sessions = data?.data || [];
    const completed = sessions.filter((session) => session.status === "COMPLETED" && session.feedback);
    const scores = completed.map(overallOf).filter((score) => score !== null);
    return {
      streak: computeStreak(sessions),
      completed: completed.length,
      average: scores.length ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length) : null,
      recent: scores.slice(-8),
    };
  }, [data]);

  return (
    <AppLayout title={t("dash.progress")} description={t("dash.progressDesc")} loading={isLoading}>
      <div className="mx-auto max-w-3xl space-y-5">
        <Card className="flex items-center gap-4 rounded-3xl border-0 bg-[#0F172A] p-6 text-white shadow-[0_16px_36px_rgba(15,23,42,0.14)]">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><Flame /></div>
          <div><p className="text-2xl font-bold">{stats.streak}-day streak</p><p className="text-sm text-white/70">Small reps add up. Keep going.</p></div>
        </Card>
        <div className="grid grid-cols-2 gap-4">
          <Card className="rounded-3xl p-5"><MessageCircle className="mb-3 text-primary" /><p className="text-3xl font-bold">{stats.completed}</p><p className="text-sm text-muted-foreground">completed sessions</p></Card>
          <Card className="rounded-3xl p-5"><Sparkles className="mb-3 text-[#5865F2]" /><p className="text-3xl font-bold">{stats.average === null ? "—" : `${stats.average}/10`}</p><p className="text-sm text-muted-foreground">average score</p></Card>
        </div>
        <Card className="rounded-3xl p-6">
          <div className="mb-5 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" /><h2 className="font-bold">Recent score trend</h2></div>
          {stats.recent.length >= 2 ? (
            <div className="flex h-32 items-end gap-3">{stats.recent.map((score, index) => <div key={index} className="flex-1 rounded-t-xl bg-primary/75" style={{ height: `${score * 10}%` }} title={`${score}/10`} />)}</div>
          ) : <p className="text-sm text-muted-foreground">Finish a couple more sessions to see your trend.</p>}
        </Card>
      </div>
    </AppLayout>
  );
}
